import { useVirtualizer } from "@tanstack/react-virtual"
import { useEffect } from "react"

interface UseInfiniteRowVirtualizerProps {
    rowCount: number
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
    fetchNextPage?: () => Promise<unknown>
    getScrollElement: () => Element | null
    estimatedRowHeight: number
    overscan?: number
}

/**
 * Virtualize an infinitely loaded list.
 *
 * Reserves one extra row for the loader while more pages exist and requests the next page as soon as
 * that row is rendered. Without the paging props it virtualizes a complete list.
 */
export default function useInfiniteRowVirtualizer(props: UseInfiniteRowVirtualizerProps) {
    const {
        rowCount,
        hasNextPage = false,
        isFetchingNextPage = false,
        fetchNextPage,
        getScrollElement,
        estimatedRowHeight,
        overscan,
    } = props

    const rowVirtualizer = useVirtualizer({
        count: hasNextPage ? rowCount + 1 : rowCount,
        getScrollElement,
        estimateSize: () => estimatedRowHeight,
        overscan,
    })

    const virtualRows = rowVirtualizer.getVirtualItems()

    useEffect(() => {
        const lastVirtualRow = virtualRows[virtualRows.length - 1]

        if (lastVirtualRow?.index === rowCount && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage?.()
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, rowCount, virtualRows])

    return { rowVirtualizer, virtualRows }
}
